/** Small shared presentational pieces. */
import { Link, useSearchParams } from 'react-router'
import { ChevronLeft, ChevronRight, LoaderCircle, TriangleAlert } from 'lucide-react'
import { ago, fullDate, num } from '../lib/format'
import type { ApiError } from '../lib/api'

export function Avatar({ name, src, size = 24 }: { name: string; src?: string | null; size?: number }) {
  const initials = name
    .split(/\s+/)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return src ? (
    <img className="avatar" src={src} alt="" width={size} height={size} referrerPolicy="no-referrer" />
  ) : (
    <span
      className="avatar avatar-blank"
      style={{ width: size, height: size, fontSize: size * 0.42 }}
      aria-hidden="true"
    >
      {initials}
    </span>
  )
}

const STATUS_TEXT: Record<string, string> = {
  draft: 'Draft',
  pending: 'Waiting for review',
  applied: 'Applied',
  rejected: 'Rejected',
  withdrawn: 'Withdrawn',
}

export function StatusBadge({ status, auto }: { status: string; auto?: boolean }) {
  return (
    <span className={`status status-${status}`}>
      {status === 'applied' && auto ? 'Applied' : (STATUS_TEXT[status] ?? status)}
    </span>
  )
}

export function Time({ ms }: { ms: number }) {
  return (
    <time dateTime={new Date(ms).toISOString()} title={fullDate(ms)}>
      {ago(ms)}
    </time>
  )
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return (
    <div className="loading" role="status">
      <LoaderCircle className="spin" size={20} aria-hidden="true" />
      <span>{label}…</span>
    </div>
  )
}

export function ErrorState({ error, retry }: { error: ApiError | Error; retry?: () => void }) {
  const status = 'status' in error ? error.status : 0
  return (
    <div className="state state-error" role="alert">
      <TriangleAlert size={22} aria-hidden="true" />
      <div>
        <h2>{status === 404 ? 'Not in the database' : 'This page could not load'}</h2>
        <p>{status === 404 ? 'It may have been merged, deleted, or the link is wrong.' : error.message}</p>
        {status === 404 ? (
          <Link to="/" className="button button-quiet">
            Back to the catalogue
          </Link>
        ) : (
          retry && (
            <button type="button" className="button button-quiet" onClick={retry}>
              Try again
            </button>
          )
        )}
      </div>
    </div>
  )
}

export function Empty({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="state state-empty">
      <h2>{title}</h2>
      {children && <div className="state-body">{children}</div>}
    </div>
  )
}

/** Page navigation that keeps the page in the URL, so back/forward and sharing work. */
export function Pager({ page, limit, total }: { page: number; limit: number; total: number }) {
  const [params, setParams] = useSearchParams()
  const pages = Math.max(1, Math.ceil(total / limit))
  if (pages <= 1) return null
  const goto = (p: number) => {
    const next = new URLSearchParams(params)
    if (p <= 1) next.delete('page')
    else next.set('page', String(p))
    setParams(next)
    document.getElementById('main')?.scrollTo?.({ top: 0 })
    window.scrollTo({ top: 0 })
  }
  const from = (page - 1) * limit + 1
  const to = Math.min(page * limit, total)
  return (
    <nav className="pager" aria-label="Pages">
      <button type="button" className="button button-quiet" disabled={page <= 1} onClick={() => goto(page - 1)}>
        <ChevronLeft size={16} aria-hidden="true" /> Previous
      </button>
      <span className="pager-info">
        {num(from)}–{num(to)} of {num(total)}
      </span>
      <button type="button" className="button button-quiet" disabled={page >= pages} onClick={() => goto(page + 1)}>
        Next <ChevronRight size={16} aria-hidden="true" />
      </button>
    </nav>
  )
}

export function Chips({ items, hrefFor }: { items: string[]; hrefFor?: (v: string) => string }) {
  if (!items.length) return null
  return (
    <ul className="chips">
      {items.map((v) => (
        <li key={v}>
          {hrefFor ? (
            <Link to={hrefFor(v)} className="chip">
              {v}
            </Link>
          ) : (
            <span className="chip">{v}</span>
          )}
        </li>
      ))}
    </ul>
  )
}

/** The page number from ?page=, defaulting to 1. */
export function usePage(): number {
  const [params] = useSearchParams()
  const p = Number.parseInt(params.get('page') ?? '1', 10)
  return Number.isFinite(p) && p > 0 ? p : 1
}
